import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './Dashboard.css';
import { subscribe as slackSubscribe, stats as getSlackStats, listMessages as slackListMessages } from 'services/signal/slackMonitoringService';

const SupplierDashboard = () => {
  const metrics = [
    {
      label: 'Active Suppliers',
      value: '234',
      trend: '+12',
      trendType: 'positive',
      icon: 'bi-people-fill'
    },
    {
      label: 'Total Contracts',
      value: '456',
      trend: '+8',
      trendType: 'positive',
      icon: 'bi-file-earmark-text'
    },
    {
      label: 'Average Response Time',
      value: '4.2d',
      trend: '-0.5',
      trendType: 'positive',
      icon: 'bi-clock-history'
    },
    {
      label: 'Compliance Rate',
      value: '94%',
      trend: '+2.1',
      trendType: 'positive',
      icon: 'bi-shield-check'
    }
  ];

  const suppliers = [
    {
      name: 'Tech Solutions Inc.',
      category: 'Technology',
      status: 'Active',
      performance: '98%',
      lastUpdated: '2 days ago'
    },
    {
      name: 'Global Logistics Co.',
      category: 'Logistics',
      status: 'Active',
      performance: '95%',
      lastUpdated: '1 day ago'
    },
    {
      name: 'Quality Services Ltd.',
      category: 'Services',
      status: 'Pending',
      performance: '87%',
      lastUpdated: '5 days ago'
    },
    {
      name: 'Innovation Partners',
      category: 'Consulting',
      status: 'Active',
      performance: '92%',
      lastUpdated: '3 days ago'
    }
  ];

  const performanceIndicators = [
    {
      label: 'On-Time Delivery',
      value: '96%',
      trend: 'positive'
    },
    {
      label: 'Quality Score',
      value: '4.8/5',
      trend: 'positive'
    },
    {
      label: 'Cost Efficiency',
      value: '92%',
      trend: 'positive'
    },
    {
      label: 'Response Rate',
      value: '98%',
      trend: 'neutral'
    }
  ];

  // Slack Monitoring summary widget state
  const [slackState, setSlackState] = useState({ messages: [], alerts: [] });
  useEffect(() => slackSubscribe(setSlackState), []);
  const slackStats = useMemo(() => getSlackStats(), [slackState]);
  const recentErrors = useMemo(() => slackListMessages().filter(m => m.status === 'error').slice(0, 3), [slackState]);

  return (
    <div className="page-container">
      <Container>
        <div className="page-header">
          <h1>Supplier Ecosystem Dashboard</h1>
          <p className="lead">Monitor and analyze supplier performance metrics</p>
        </div>

        <div className="metrics-grid">
          {metrics.map((metric, index) => (
            <Card key={index} className="metric-card">
              <Card.Body className="text-center">
                <i className={`bi ${metric.icon} metric-icon`}></i>
                <div className="metric-value">{metric.value}</div>
                <div className="metric-label">{metric.label}</div>
                <div className={`trend-indicator trend-${metric.trendType}`}>
                  <i className={`bi ${metric.trendType === 'positive' ? 'bi-arrow-up' : 'bi-arrow-down'}`}></i>
                  {metric.trend}
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>

        <Row>
          <Col lg={8}>
            <Card className="mb-4">
              <Card.Body>
                <h4>Recent Supplier Activity</h4>
                <Table className="supplier-table">
                  <thead>
                    <tr>
                      <th>Supplier Name</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Performance</th>
                      <th>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map((supplier, index) => (
                      <tr key={index}>
                        <td>{supplier.name}</td>
                        <td>{supplier.category}</td>
                        <td>
                          <span className={`status-badge status-${supplier.status.toLowerCase()}`}>
                            {supplier.status}
                          </span>
                        </td>
                        <td>{supplier.performance}</td>
                        <td>{supplier.lastUpdated}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="mb-4">
              <Card.Body>
                <h4>Performance Indicators</h4>
                <div className="performance-indicators">
                  {performanceIndicators.map((indicator, index) => (
                    <div key={index} className="indicator-card">
                      <div className="indicator-value">{indicator.value}</div>
                      <div className="indicator-label">{indicator.label}</div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="mb-0">Slack Integration</h4>
                  <Link to="/supplier-ecosystem/dashboard/slack-monitoring" className="btn btn-sm btn-outline-primary">Open</Link>
                </div>
                <div className="d-flex gap-3 mb-3">
                  <div>
                    <div className="small text-muted">Events</div>
                    <div className="metric-value" style={{ fontSize: 22 }}>{slackStats.total || 0}</div>
                  </div>
                  <div>
                    <div className="small text-muted">Success</div>
                    <div className="metric-value" style={{ fontSize: 22 }}>{slackStats.success || 0}</div>
                  </div>
                  <div>
                    <div className="small text-muted">Errors</div>
                    <div className="metric-value" style={{ fontSize: 22, color: '#dc2626' }}>{slackStats.errors || 0}</div>
                  </div>
                </div>
                <div>
                  <div className="small text-muted mb-2">Recent Errors</div>
                  {recentErrors.length === 0 && (
                    <div className="text-muted">No recent errors</div>
                  )}
                  {recentErrors.map((e) => (
                    <div key={e.id} className="d-flex align-items-center mb-1" style={{ fontSize: 14 }}>
                      <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#dc2626' }} />
                      <span className="me-2" style={{ whiteSpace: 'nowrap' }}>{new Date(e.ts).toLocaleTimeString()}</span>
                      <span className="text-truncate" title={e.text}>{e.text}</span>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SupplierDashboard;
